import { CheckCircle, Lock, Share } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API } from "@/lib/api";

/**
 * Read-only badge grid. Used on:
 *   - Dashboard (data-testid=my-badges) for the current user
 *   - PublicTraderPage (data-testid=trader-badges) for anyone's profile
 *
 * Locked badges are shown in a dim state so users can see what to aim for next.
 * Earned badges get a Share button that copies a link to the OG share card.
 */
export default function BadgeGrid({ badges, dataTestId, ownerTag }) {
  if (!badges || badges.length === 0) return null;

  const shareBadge = async (b) => {
    const tag = ownerTag || "trader_anon";
    const ogUrl = `${API}/badges/${b.id}/og.png?tag=${encodeURIComponent(tag)}`;
    const shareText = `I just earned the "${b.label}" badge on STRATOS ${b.emoji} — paper-trading bots against the community. ${ogUrl}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `STRATOS badge — ${b.label}`, text: shareText, url: ogUrl });
        toast.success("Shared");
        return;
      }
    } catch {}
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success("Share text + OG image link copied");
    } catch {
      toast.error("Could not copy — long-press to copy manually");
    }
  };

  return (
    <div
      data-testid={dataTestId}
      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2"
    >
      {badges.map((b) => {
        const progress = b.progress
          ? `${b.progress.have} / ${b.progress.need}`
          : null;
        return (
          <div
            key={b.id}
            data-testid={`badge-${b.id}`}
            className={`p-3 border rounded-sm flex flex-col items-start gap-1.5 relative ${
              b.earned
                ? "border-[#00C805]/60 bg-[#00C805]/5"
                : "border-zinc-800 bg-zinc-900/40 opacity-70"
            }`}
            title={b.description}
          >
            <div className="flex items-center gap-2 w-full">
              <span className="text-xl leading-none">{b.emoji}</span>
              {b.earned ? (
                <CheckCircle size={12} weight="fill" className="text-[#00C805]" />
              ) : (
                <Lock size={11} className="text-zinc-600" />
              )}
              {b.earned && (
                <button
                  onClick={() => shareBadge(b)}
                  data-testid={`badge-share-${b.id}`}
                  title={`Share ${b.label}`}
                  className="ml-auto text-zinc-500 hover:text-[#00C805] p-0.5"
                >
                  <Share size={11} />
                </button>
              )}
            </div>
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-300 leading-tight">
              {b.label}
            </div>
            {progress && !b.earned && (
              <div className="mono text-[10px] text-zinc-500">{progress}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
